import type { AuthModel, SendOptions, RecordListOptions } from 'pocketbase';

export const useDbStore = defineStore('db', () => {
  const { $pb } = useNuxtApp();

  const getProducts = async () => {
    return $pb.collection('products').getFullList({ sort: 'created' });
  };

  const logout = async () => {
    console.info('logged out');
    // this.unsubscribeFromAllCollections();
    $pb.collection('users').unsubscribe();
    $pb.collection('orders').unsubscribe();
    $pb.collection('deliveryDetails').unsubscribe();
    $pb.collection('bonuses').unsubscribe();
    // user.value = null;
    return $pb.authStore.clear();
  };

  // console.log($pb);
  const authRefresh = async () => {
    return $pb.collection('users').authRefresh({ expand: 'orders,deliveryList,referralList,bonuses,bonuses.user,customizerPresets' });
  };

  const checkValid = async () => {
    authRefresh().catch(() => {
      logout();
    });

    return $pb.authStore.isValid;
  };

  const createUser = async (email: string, password: string, passwordConfirm: string, name: string, referralUser?: string) => {
    return $pb.collection('users').create({
      name,
      email,
      password,
      passwordConfirm,
      ...(referralUser ? { referralUser } : {}),
    });
  };

  const loginByEmail = async (email: string, password: string) => {
    return $pb.collection('users').authWithPassword(email, password, { expand: 'orders,deliveryList,referralList,bonuses,bonuses.user,customizerPresets' });
  };

  const loginByGoogle = async (referralUser: string) => {
    const options: {
      provider: 'google';
      urlCallback?: (url: string) => void;
      expand: string;
      createData: {
        referralUser?: string;
      };
    } = {

      provider: 'google',
      expand: 'orders,deliveryList,referralList,bonuses,bonuses.user,customizerPresets',
      createData: { ...(referralUser ? { referralUser } : {}) },
    };

    const { isMobile } = useDevice();

    const w = isMobile ? (window.open() as Window | null) : null;

    if (w) {
      options.urlCallback = (url) => {
        w.location.href = url;
      };
    }

    const authResponse = $pb.collection('users').authWithOAuth2(options);
    // console.log('loginByGoogle', authResponse);
    return authResponse;
  };

  const loginByApple = async () => {
    // TODO: implement login
  };

  const onValidationChange = async (cb: (record: AuthModel) => void) => {
    $pb.authStore.onChange((token, model) => {
      // console.log('onValidationChange MODEL', model);
      cb?.(model);
    });
  };

  const subscribeToCollection = (
    collectionName: 'users' | 'orders' | 'deliveryDetails' | 'bonuses' | 'customizerPresets',
    cb: (data: any) => void,
    id = '*',
    options?: SendOptions,
  ) => {
    // console.log('subscribe', collectionName);
    $pb.collection(collectionName).subscribe(id, cb, options);
  };

  const unsubscribeFromCollection = (collectionName: 'users' | 'orders' | 'deliveryDetails' | 'bonuses' | 'customizerPresets', id = '') => {
    $pb.collection(collectionName).unsubscribe(id);
  };

  const unsubscribeFromAllCollections = () => {
    $pb.collection('users').unsubscribe();
    $pb.collection('orders').unsubscribe();
    $pb.collection('deliveryDetails').unsubscribe();
    $pb.collection('bonuses').unsubscribe();
    $pb.collection('customizerPresets').unsubscribe();
  };

  const createOrder = async (order: ServerOrderI) => {
    // console.log('CREATED ORDER ON SERVER', order);
    return $pb.collection('orders').create(order);
  };

  const updateOrder = async (id: string, orderPartial: Partial<ServerOrderI>) => {
    return $pb.collection('orders').update(id, orderPartial);
  };

  const addOrderApproveFiles = async (id: string, files: File[]) => {
    return $pb.collection('orders').update(id, {
      'approveFiles+': files,
    });
  };
  const addOrderFiles = async (id: string, files: File[]) => {
    return $pb.collection('orders').update(id, {
      'files+': files,
    });
  };

  const removeOrderFile = async (id: string, fileName: string) => {
    return $pb.collection('orders').update(id, {
      'files-': [fileName],
    });
  };
  const removeOrderApproveFile = async (id: string, fileName: string) => {
    return $pb.collection('orders').update(id, {
      'approveFiles-': [fileName],
    });
  };

  const deleteOrder = async (id: string) => {
    return $pb.collection('orders').delete(id);
  };

  const getOrders = async (options: RecordListOptions) => {
    return $pb.collection('orders').getFullList({ expand: 'user,invoices', ...options });
  };

  const getOrder = async (id: string) => {
    return $pb.collection('orders').getOne(id);
  };

  const addAddress = async (address: Omit<AddressI, 'id'>) => {
    return $pb.collection('deliveryDetails').create(address);
  };

  const removeAddress = async (id: string) => {
    return $pb.collection('deliveryDetails').delete(id);
  };

  const updateAddress = async (id: string, address: AddressI) => {
    return $pb.collection('deliveryDetails').update(id, address);
  };

  const addCustomizerPresets = async (preset: Omit<CustomizerPresetI, 'id' | 'user'>) => {
    return $pb.collection('customizerPresets').create({ ...preset, user: $pb.authStore.model!.id });
  };

  const removeCustomizerPresets = async (id: string) => {
    return $pb.collection('customizerPresets').delete(id);
  };

  const updateCustomizerPresets = async (id: string, preset: Partial<CustomizerPresetI>) => {
    return $pb.collection('customizerPresets').update(id, preset);
  };

  const resetPassword = async (email: string) => {
    return $pb.collection('users').requestPasswordReset(email);
  };

  const confirmResetPassword = async (token: string, password: string, confirmPassword: string) => {
    return $pb.collection('users').confirmPasswordReset(token, password, confirmPassword);
  };

  const requestVerification = async (email: string) => {
    return $pb.collection('users').requestPasswordReset(email);
  };

  const confirmVerification = async (token: string) => {
    return $pb.collection('users').requestPasswordReset(token);
  };

  const updateUserData = async (data: Record<string, any>) => {
    // console.log('updateUserData', data);

    if (!$pb.authStore.model) {
      console.warn('unauthorized');
      return;
    };
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      formData.append(key, data[key]);
    });
    return $pb.collection('users').update($pb.authStore.model.id, data);
  };

  const createPayment = async (paymentDetails: any) => {
    if (!$pb.authStore.model?.id) return;

    return await $pb.collection('payments').create({
      details: paymentDetails,
      user: $pb.authStore.model!.id,
      // orders: orderIds,
    });
  };

  const checkReferralAvailable = async (referralCode: string) => {
    return $pb.send(`api/referralAvalaible/${referralCode}`, {
      method: 'GET',
    });
  };

  const createSingleInvoice = async (bonuses: number, id: string) => {
    console.log(bonuses, id);
    return $pb.send(`api/createSingleInvoice/${bonuses}/${id}`, {
      method: 'POST',
    });
  };

  const createAdditionalInvoice = async (id: string) => {
    return $pb.send(`api/createAdditionalInvoice/${id}`, {
      method: 'POST',
    });
  };

  const requestInvoice = async (ids: string[], email: string, discount = 0) => {
    console.log(discount);
    return $pb.send(`api/requestInvoice`, {
      method: 'POST',
      body: JSON.stringify({
        ids,
        email,
        discount,
      }),
    });
  };

  const setInvoiceStatus = async (id: string, status: InvoiceStatusT) => {
    return $pb.send(`api/invoiceStatus/${id}/${status}`, {
      method: 'POST',
    });
  };

  const movePreorderToCart = async (id: string) => {
    return $pb.send(`api/movePreorderToCart/${id}`, {
      method: 'POST',
    });
  };

  const createPrecartOrder = async () => {
    return $pb.send(`api/createPrecartOrder`, {
      method: 'POST',
    });
  };

  return {
    getProducts,

    updateUserData,

    checkValid,
    authRefresh,
    createUser,
    loginByEmail,
    loginByGoogle,
    loginByApple,
    logout,

    resetPassword,
    confirmResetPassword,
    requestVerification,
    confirmVerification,

    onValidationChange,

    createOrder,
    updateOrder,
    addOrderApproveFiles,
    addOrderFiles,
    removeOrderFile,
    removeOrderApproveFile,
    deleteOrder,
    getOrders,
    getOrder,

    addCustomizerPresets,
    removeCustomizerPresets,
    updateCustomizerPresets,

    addAddress,
    removeAddress,
    updateAddress,

    subscribeToCollection,
    unsubscribeFromCollection,
    unsubscribeFromAllCollections,

    createPayment,

    checkReferralAvailable,

    createSingleInvoice,
    createAdditionalInvoice,
    requestInvoice,
    movePreorderToCart,
    createPrecartOrder,
    setInvoiceStatus,
    auth: $pb.authStore,
  };
});
